import { useLocation, useRoute } from 'wouter'
import { ChevronLeft, Calendar, User, Loader2, Share2 } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Button } from '@/components/ui/button'
import useSWR from 'swr'
import { apiFetch } from '@/utils'
import { useToast } from '@/hooks/use-toast'
import { getImageUrls } from '@/utils/image-urls'
import GalleryGrid, { GalleryImage } from '@/components/GalleryGrid'

interface NewsArticleDetail {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
  author?: number
  gallery?: GalleryImage[]
}

const LoadingState = () => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#EF4444] animate-spin" />
      <p className="text-[#BABABB] text-sm mt-4">Loading article...</p>
    </div>
  )
}

const ErrorState = ({ message }: { message?: string }) => {
  const [, navigate] = useLocation()

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6">
      <p className="text-[#EF4444] text-center mb-4">
        {message || 'Failed to load article. Please try again later.'}
      </p>
      <Button
        onClick={() => navigate('/news')}
        className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
      >
        Back to News
      </Button>
    </div>
  )
}

export default function NewsDetailPage() {
  const [, navigate] = useLocation()
  const [match, params] = useRoute('/news/:id')
  const { toast } = useToast()

  const articleId = params?.id

  const { data, error, isLoading } = useSWR(
    articleId ? `/v1/secrets_hideaway/news/${articleId}` : null,
    (url: string) => apiFetch(url, { withCredentials: true }).then((res) => res.json())
  )

  const article: NewsArticleDetail | null = data?.success && data?.details?.items?.[0] ? data.details.items[0] : null
  const imageUrls = article?.filename ? getImageUrls('news', article.filename) : null

  const formattedDate = article?.published_date
    ? new Date(article.published_date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.heading || 'News Article',
          url
        })
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(url)
      toast({
        title: 'Link copied',
        description: 'Article link copied to clipboard'
      })
    }
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-[#18181B] flex flex-col">
        <div className="max-w-3xl mx-auto w-full px-5 pb-8">
          {/* Header with back button */}
          <div
            role="banner"
            className="h-12 flex items-center justify-center text-white mb-4 pt-3 relative"
          >
            <button
              type="button"
              aria-label="Go back"
              onClick={() => navigate('/news')}
              className="absolute left-0
                h-8 w-8
                flex items-center justify-center
                rounded-full
                bg-white/10 hover:bg-white/20
                focus:outline-none focus:ring-2 focus:ring-white/40"
            >
              <ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
            </button>

            <h1 className="text-base font-semibold tracking-wide">
              News
            </h1>

            {article && (
              <button
                type="button"
                aria-label="Share article"
                onClick={handleShare}
                className="absolute right-0
                  h-8 w-8
                  flex items-center justify-center
                  rounded-full
                  bg-white/10 hover:bg-white/20
                  focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <Share2 className="h-[18px] w-[18px] text-[#ffffff]" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <LoadingState />
        ) : error || !article ? (
          <ErrorState />
        ) : (
          <div className="flex-1 max-w-3xl mx-auto w-full px-5 pb-8">
            {/* Title */}
            <h2 className="text-[#ffffff] font-bold text-2xl mb-4">
              {article.heading}
            </h2>

            {/* Meta Info */}
            <div className="flex items-center gap-4 mb-6">
              {formattedDate && (
                <div className="flex items-center gap-2 text-[#BABABB] text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>{formattedDate}</span>
                </div>
              )}
            </div>

            {/* Featured Image */}
            {imageUrls && (
              <div className="aspect-video w-full overflow-hidden rounded-xl mb-6">
                <img
                  src={imageUrls.landscape}
                  alt={article.filename_alt || article.heading}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Summary */}
            {article.page_description && (
              <p className="text-[#ffffff] text-lg leading-relaxed mb-6 font-medium">
                {article.page_description}
              </p>
            )}

            {/* Full Content */}
            {article.content && (
              <div className="prose prose-invert max-w-none">
                <div
                  className="text-[#BABABB] leading-relaxed [&>p]:mb-4 [&>h2]:text-[#ffffff] [&>h2]:font-semibold [&>h2]:text-xl [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:text-[#ffffff] [&>h3]:font-semibold [&>h3]:text-lg [&>h3]:mt-6 [&>h3]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-4 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-4 [&>a]:text-[#EF4444] [&>a]:hover:text-[#DC2626]"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            )}

            {/* Gallery Grid */}
            {article.gallery && article.gallery.length > 0 && (
              <GalleryGrid images={article.gallery} title="Gallery" />
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}
